import React, { useEffect, useState } from 'react';
import CapAccessManager from './CapAccessManager';

const API = process.env.REACT_APP_API_URL;

const STATUS_LABEL = {
  open:        { label: 'Open',           tone: 'bg-amber-50 text-amber-800 ring-amber-200' },
  in_progress: { label: 'In progress',    tone: 'bg-blue-50 text-blue-800 ring-blue-200' },
  validating:  { label: 'Validating',     tone: 'bg-violet-50 text-violet-800 ring-violet-200' },
  approved:    { label: 'Approved',       tone: 'bg-emerald-50 text-emerald-800 ring-emerald-200' },
  closed:      { label: 'Closed',         tone: 'bg-slate-100 text-slate-700 ring-slate-200' },
  cancelled:   { label: 'Cancelled',      tone: 'bg-slate-100 text-slate-500 ring-slate-200' },
};

const URGENCY_LABEL = {
  critical: { label: 'Critical', tone: 'bg-red-50 text-red-800 ring-red-200' },
  high:     { label: 'High',     tone: 'bg-orange-50 text-orange-800 ring-orange-200' },
  medium:   { label: 'Medium',   tone: 'bg-slate-100 text-slate-700 ring-slate-200' },
  low:      { label: 'Low',      tone: 'bg-slate-50 text-slate-500 ring-slate-200' },
};

function Pill({ children, tone }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ${tone}`}>
      {children}
    </span>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function CapList({ user, onOpen, onNew, onReports }) {
  const [caps, setCaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [error, setError] = useState(null);

  // Extra filters (fed to the backend; not hardcoded)
  const [departments, setDepartments] = useState([]);
  const [filterDept, setFilterDept] = useState('');
  const [filterUrgency, setFilterUrgency] = useState('');
  const [filterDueFrom, setFilterDueFrom] = useState('');
  const [filterDueTo, setFilterDueTo] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Authorization state: who can create CAPs, who can manage the grants.
  // Both flags come from the server — never compute these client-side.
  const [canCreate, setCanCreate] = useState(false);
  const [canManageAccess, setCanManageAccess] = useState(false);
  const [showAccessManager, setShowAccessManager] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/pcap/access/can-create`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        if (d?.success) {
          setCanCreate(!!d.allowed);
          setCanManageAccess(!!d.can_manage);
        }
      })
      .catch(() => {});
  }, []);

  // Pull departments scoped to the caller (compliance gets all; dept-bound only their own).
  useEffect(() => {
    fetch(`${API}/api/pcap/departments`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (d.success) setDepartments(d.departments || []); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (filter !== 'all') params.set('status', filter);
    if (filterDept) params.set('department_id', filterDept);
    if (filterUrgency) params.set('urgency', filterUrgency);
    if (filterDueFrom) params.set('due_from', filterDueFrom);
    if (filterDueTo) params.set('due_to', filterDueTo);
    if (searchQ.trim()) params.set('q', searchQ.trim());
    fetch(`${API}/api/pcap/caps?${params.toString()}`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        if (data.success) setCaps(data.caps || []);
        else setError(data.message || 'Failed to load CAPs');
      })
      .catch(err => !cancelled && setError(err.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [filter, filterDept, filterUrgency, filterDueFrom, filterDueTo, searchQ]);

  const hasActiveExtraFilter = !!(filterDept || filterUrgency || filterDueFrom || filterDueTo || searchQ);
  const clearExtraFilters = () => {
    setFilterDept(''); setFilterUrgency('');
    setFilterDueFrom(''); setFilterDueTo('');
    setSearchQ('');
  };

  const counts = caps.reduce((acc, c) => { acc[c.status] = (acc[c.status] || 0) + 1; return acc; }, {});
  const FILTERS = [
    { id: 'all',         label: 'All',         count: caps.length },
    { id: 'open',        label: 'Open',        count: counts.open || 0 },
    { id: 'in_progress', label: 'In progress', count: counts.in_progress || 0 },
    { id: 'validating',  label: 'Validating',  count: counts.validating || 0 },
    { id: 'closed',      label: 'Closed',      count: counts.closed || 0 },
  ];

  return (
    <div className="max-w-6xl mx-auto px-6 pt-10 pb-16">
      <div className="flex items-end justify-between mb-7 gap-4 flex-wrap">
        <div>
          <h1 className="text-[26px] font-semibold text-slate-900 tracking-tight">Corrective action plans</h1>
          <p className="text-sm text-slate-500 mt-1.5">
            One place to work the finding, the why, the remediation, and the evidence it worked.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canManageAccess && (
            <button
              onClick={() => setShowAccessManager(true)}
              title="Manage CAP creation access"
              className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-white text-slate-700 ring-1 ring-slate-200 text-sm font-medium hover:bg-slate-50 transition"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
              Access
            </button>
          )}
          {onReports && (
            <button
              onClick={onReports}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-white ring-1 ring-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50 transition"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" /></svg>
              Reports
            </button>
          )}
          {canCreate && (
            <button
              onClick={onNew}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition shadow-[0_1px_2px_rgba(15,23,42,0.06)]"
            >
              <span className="text-base leading-none">+</span> New CAP
            </button>
          )}
        </div>
      </div>

      <CapAccessManager
        open={showAccessManager}
        onClose={() => setShowAccessManager(false)}
      />

      <div className="flex items-center gap-1 mb-3 flex-wrap">
        {FILTERS.map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 text-sm rounded-md transition ${
              filter === f.id
                ? 'bg-white text-slate-900 ring-1 ring-slate-200 shadow-sm'
                : 'text-slate-600 hover:bg-white/60'
            }`}
          >
            {f.label} <span className="text-slate-400 ml-1">{f.count}</span>
          </button>
        ))}
        <div className="flex-1" />
        <input
          type="search"
          value={searchQ}
          onChange={e => setSearchQ(e.target.value)}
          placeholder="Search title or deficiency"
          className="rounded-md border border-slate-200 px-2.5 py-1.5 text-sm w-56 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
        />
        <button
          type="button"
          onClick={() => setShowFilters(v => !v)}
          className={`px-3 py-1.5 text-sm rounded-md transition ${
            hasActiveExtraFilter
              ? 'bg-slate-900 text-white'
              : 'text-slate-600 hover:bg-white/60 ring-1 ring-slate-200 bg-white'
          }`}
        >
          Filters{hasActiveExtraFilter ? ' · on' : ''}
        </button>
      </div>

      {showFilters && (
        <div className="bg-white rounded-xl ring-1 ring-slate-200 p-4 mb-4 grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div>
            <label className="block text-[12px] text-slate-500 mb-1">Department</label>
            <select
              value={filterDept}
              onChange={e => setFilterDept(e.target.value)}
              className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
            >
              <option value="">Any</option>
              {departments.map(d => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[12px] text-slate-500 mb-1">Urgency</label>
            <select
              value={filterUrgency}
              onChange={e => setFilterUrgency(e.target.value)}
              className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
            >
              <option value="">Any</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
          <div>
            <label className="block text-[12px] text-slate-500 mb-1">Due from</label>
            <input
              type="date"
              value={filterDueFrom}
              onChange={e => setFilterDueFrom(e.target.value)}
              className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
            />
          </div>
          <div>
            <label className="block text-[12px] text-slate-500 mb-1">Due to</label>
            <input
              type="date"
              value={filterDueTo}
              onChange={e => setFilterDueTo(e.target.value)}
              className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
            />
          </div>
          {hasActiveExtraFilter && (
            <div className="sm:col-span-4">
              <button
                type="button"
                onClick={clearExtraFilters}
                className="text-[12px] text-slate-500 hover:text-slate-800 underline underline-offset-2"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="rounded-lg bg-red-50 ring-1 ring-red-200 p-3 text-sm text-red-800 mb-4">
          {error}
        </div>
      )}

      <div className="bg-white rounded-2xl ring-1 ring-slate-200/80 shadow-[0_1px_2px_rgba(15,23,42,0.04)] divide-y divide-slate-100 overflow-hidden">
        {loading && (
          <div className="p-6 text-sm text-slate-500">Loading…</div>
        )}
        {!loading && caps.length === 0 && (
          <div className="p-10 text-center">
            <p className="text-sm text-slate-500">No CAPs yet.</p>
            <button
              onClick={onNew}
              className="mt-3 text-sm text-slate-900 underline underline-offset-4"
            >
              Create the first one
            </button>
          </div>
        )}
        {!loading && caps.map(cap => {
          const s = STATUS_LABEL[cap.status] || STATUS_LABEL.open;
          const u = URGENCY_LABEL[cap.urgency] || URGENCY_LABEL.medium;
          return (
            <button
              key={cap.id}
              onClick={() => onOpen(cap.id)}
              className="w-full text-left px-5 py-4 hover:bg-slate-50 transition flex items-start gap-4"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Pill tone={s.tone}>{s.label}</Pill>
                  <Pill tone={u.tone}>{u.label}</Pill>
                  {cap.is_fdr_scope && (
                    <Pill tone="bg-orange-50 text-orange-800 ring-orange-200">FDR scope</Pill>
                  )}
                </div>
                <p className="text-sm font-medium text-slate-900 truncate">{cap.title}</p>
                {cap.deficiency && (
                  <p className="text-[13px] text-slate-500 truncate mt-0.5">{cap.deficiency}</p>
                )}
              </div>
              <div className="text-right text-[12px] text-slate-500 shrink-0">
                <div>Due {fmtDate(cap.due_date)}</div>
                <div className="text-slate-400">Opened {fmtDate(cap.created_at)}</div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
